import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import { pickDocumentFile } from "@/lib/native/pickDocumentFile";
import {
  pickNativeCameraPhoto,
  pickNativeGalleryPhoto,
} from "@/lib/native/nativeImagePicker";

export type OpenFilePickerOptions = {
  accept?: string;
  multiple?: boolean;
  /** Native image/* — use camera instead of gallery */
  preferCamera?: boolean;
};

function isImageAccept(accept?: string): boolean {
  if (!accept) return false;
  return accept.includes("image") && !accept.includes("pdf");
}

function pickViaHiddenInput(options: OpenFilePickerOptions): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (options.accept) input.accept = options.accept;
    input.multiple = options.multiple ?? false;
    if (options.preferCamera) {
      input.capture = "environment";
    }
    input.style.cssText =
      "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;";

    let settled = false;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      input.removeEventListener("change", onChange);
      window.removeEventListener("focus", onWindowFocus);
      input.remove();
      resolve(files);
    };

    const onChange = () => {
      finish(Array.from(input.files ?? []));
    };

    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (!settled && !input.files?.length) finish([]);
      }, 500);
    };

    input.addEventListener("change", onChange);
    window.addEventListener("focus", onWindowFocus);
    document.body.appendChild(input);
    input.click();
  });
}

/** Unified file/image picker for web and native Capacitor. */
export async function openFilePicker(
  options: OpenFilePickerOptions = {},
): Promise<File[]> {
  if (!isNativeCapacitorApp()) {
    return pickViaHiddenInput(options);
  }

  if (isImageAccept(options.accept)) {
    if (options.multiple) {
      return pickViaHiddenInput(options);
    }
    const file = options.preferCamera
      ? await pickNativeCameraPhoto()
      : await pickNativeGalleryPhoto();
    return file ? [file] : [];
  }

  if (options.multiple) {
    return pickViaHiddenInput(options);
  }

  const file = await pickDocumentFile({
    accept: options.accept,
    multiple: false,
  });
  return file ? [file] : [];
}
